import { Injectable } from '@angular/core'
import posthog from 'posthog-js'

import { ENV_CONFIG } from '../../bin/env.config'

/**
 * PostHog feature flag keys used by the app. Keys must match the flag key in
 * the PostHog dashboard exactly. Remove entries here once a flag is fully
 * rolled out and the gate is deleted.
 */
export const FEATURE_FLAGS = {
  /** Gates the "Connect with co-workers" feature (/connections). */
  connectableAccounts: 'connectable-accounts',
  /**
   * Gates bulk text marketing (Marketing > Text, /text-marketing). Rollout
   * only — the page still enforces its own admin + subscription eligibility.
   */
  bulkText: 'bulk-text'
} as const

/**
 * Key of a flag declared above. Flag-taking methods accept only these, so a
 * typo is a compile error rather than a flag that silently reads as disabled.
 */
export type FeatureFlagKey = typeof FEATURE_FLAGS[keyof typeof FEATURE_FLAGS]

/**
 * Thin wrapper around posthog-js so pages and services depend on a typed
 * Angular service instead of touching the global `window.posthog`.
 *
 * Init runs in the constructor so any consumer injected during Angular DI
 * (notably UserService, which calls identify() on session-restore from its
 * own constructor) sees an already-initialized SDK. AppComponent no longer
 * calls init().
 *
 * Every capture/identify/reset call is wrapped in try/catch: analytics
 * failures must never break signup, login, or navigation. Calls made before
 * init completes are silently dropped.
 *
 * Config MUST match the marketing site's posthog.init config so the
 * `distinct_id` cookie set on salonmonster.com is reused when a visitor
 * lands on #/register.
 */
@Injectable({ providedIn: 'root' })
export class PostHogService {
  private initialized = false

  constructor () {
    const key = ENV_CONFIG.posthogKey
    const host = ENV_CONFIG.posthogHost

    if (!key || !host) {
      console.warn('[PostHogService] posthogKey/posthogHost missing from ENV_CONFIG; skipping init')
      return
    }

    try {
      posthog.init(key, {
        api_host: host,
        // Marketing site uses `true` (multi-page static site). This is an SPA,
        // so we fire $pageview manually on NavigationEnd in AppComponent.
        capture_pageview: false,
        // Off: $pageleave sends `$current_url` which includes hash-route fragments
        // like #/clients/12345 — we don't want third-party DB IDs in PostHog.
        capture_pageleave: false,
        autocapture: false,
        persistence: 'localStorage+cookie',
        // Admin surface with client/PII data — never record sessions/heatmaps.
        disable_session_recording: true
      })
      this.initialized = true
    } catch (err) {
      console.error('[PostHogService] init failed', err)
    }
  }

  public capture (event: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.capture(event, properties)
    } catch (err) {
      console.error('[PostHogService] capture failed', event, err)
    }
  }

  public capturePageview (currentUrl: string, properties?: Record<string, unknown>): void {
    this.capture('$pageview', { $current_url: currentUrl, ...(properties || {}) })
  }

  public identify (distinctId: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.identify(distinctId, properties)
    } catch (err) {
      console.error('[PostHogService] identify failed', err)
    }
  }

  /**
   * Current anonymous/visitor id (PostHog `distinct_id`). Captured BEFORE
   * `identify()` is called, this is the pre-registration visitor id — bridged
   * from the marketing site — used to stitch the anonymous visitor to the
   * canonical user server-side: the API forwards it to Segment as the
   * `anonymousId` on the first identify/track after account creation.
   */
  public getAnonymousId (): string | undefined {
    if (!this.initialized) {
      return undefined
    }
    try {
      return posthog.get_distinct_id()
    } catch (err) {
      console.error('[PostHogService] get_distinct_id failed', err)
      return undefined
    }
  }

  // Super properties — attached to every subsequent event in the session
  // (and persisted across reloads). Cleared by reset() on logout. Used by
  // the signup flow to keep UTMs glued to every funnel event without having
  // to spread them at each capture() call.
  public register (properties: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.register(properties)
    } catch (err) {
      console.error('[PostHogService] register failed', err)
    }
  }

  // Person properties on the currently-identified user. `setOnce` uses
  // PostHog's $set_once semantics — values only land if the property isn't
  // already set, which is the right behaviour for first-touch attribution
  // (e.g. initial_utm_source must not be overwritten on later logins).
  public setPersonProperties (
    set?: Record<string, unknown>,
    setOnce?: Record<string, unknown>
  ): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.setPersonProperties(set, setOnce)
    } catch (err) {
      console.error('[PostHogService] setPersonProperties failed', err)
    }
  }

  /**
   * Properties used ONLY to evaluate feature flags. They ride along in the
   * `person_properties` field of the /flags request and are never written to
   * a person profile, so release conditions can be matched without
   * identifying anyone — the case an admin impersonation session needs, where
   * identify() is deliberately skipped but the session must still resolve the
   * impersonated salon's flags.
   *
   * Triggers a flag reload (posthog-js default), so callers that read a flag
   * straight afterwards should use waitForFreshFeatureFlag() rather than the
   * cached read, exactly as they must after identify().
   *
   * Cleared by reset() on logout, which is what stops one session's salon
   * leaking into the next.
   */
  public setPersonPropertiesForFlags (properties: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.setPersonPropertiesForFlags(properties)
    } catch (err) {
      console.error('[PostHogService] setPersonPropertiesForFlags failed', err)
    }
  }

  /**
   * Current value of a boolean feature flag. Returns false (never undefined)
   * when flags haven't loaded yet, the SDK isn't initialized, or the flag
   * doesn't exist — gated features must default to hidden, not flash on.
   *
   * Flags load asynchronously shortly after init and reload after every
   * identify()/reset(), so a cold start can briefly report false for a flag
   * that is actually on. Consumers that need to react when the real values
   * arrive should use onFeatureFlags() (menus) or waitForFeatureFlag()
   * (route guards) instead of polling this.
   */
  public isFeatureEnabled (flag: FeatureFlagKey): boolean {
    if (!this.initialized) {
      return false
    }
    try {
      return posthog.isFeatureEnabled(flag) === true
    } catch (err) {
      console.error('[PostHogService] isFeatureEnabled failed', flag, err)
      return false
    }
  }

  /**
   * Runs the callback every time feature flags finish (re)loading — on
   * initial fetch, after identify() on login, and after reset() on logout.
   * Fires immediately if flags are already loaded. Never fires when the SDK
   * failed to initialize, so callbacks must not be the only path to a sane
   * default state.
   *
   * Returns posthog's unsubscribe function so short-lived consumers (route
   * guards via waitForFeatureFlag) can detach; long-lived consumers (the
   * AppComponent menu) can ignore it. Always returns a callable — a no-op
   * when the SDK is uninitialized or registration threw — so callers never
   * have to null-check before cleaning up.
   */
  public onFeatureFlags (callback: () => void): () => void {
    if (!this.initialized) {
      return () => undefined
    }
    try {
      return posthog.onFeatureFlags(() => callback()) ?? (() => undefined)
    } catch (err) {
      console.error('[PostHogService] onFeatureFlags failed', err)
      return () => undefined
    }
  }

  /**
   * Resolves with the flag's value once flags have loaded, or with the
   * current (cached-or-false) value after `timeoutMs` if the network fetch
   * never completes — offline devices fall back to the last values persisted
   * in localStorage rather than hanging navigation forever.
   */
  public waitForFeatureFlag (flag: FeatureFlagKey, timeoutMs: number = 3000): Promise<boolean> {
    return this.waitForFlagSettle(flag, timeoutMs, { freshFetch: false })
  }

  /**
   * Like waitForFeatureFlag(), but ignores the synchronous cached-flags fire
   * and forces a fresh flag fetch before settling. Needed when the flag
   * targets person properties set by an identify() that JUST happened:
   * onFeatureFlags fires immediately with the stale pre-identify values when
   * flags were already loaded, and identify()'s own reload lands later.
   *
   * Resolves FALSE (not the cached value) on timeout: a fresh fetch exists
   * to pick up a just-changed targeting decision, and falling back to a
   * cached `true` would keep a rolled-back salon on the flagged path through
   * a PostHog outage. Known bounded staleness: a flags response already in
   * flight when this registers (e.g. from a pre-identify reload) can settle
   * the wait with pre-identify values — the failure direction is the safe
   * default (false) and the next login re-resolves.
   */
  public waitForFreshFeatureFlag (flag: FeatureFlagKey, timeoutMs: number = 3000): Promise<boolean> {
    return this.waitForFlagSettle(flag, timeoutMs, { freshFetch: true })
  }

  /**
   * Shared settle machinery for the two flag waits. `freshFetch` skips the
   * registration-time synchronous fire, triggers a flag reload, and makes
   * the timeout resolve false instead of the cached value.
   */
  private waitForFlagSettle (
    flag: FeatureFlagKey,
    timeoutMs: number,
    opts: { freshFetch: boolean }
  ): Promise<boolean> {
    if (!this.initialized) {
      return Promise.resolve(false)
    }
    return new Promise<boolean>((resolve) => {
      let settled = false
      let registering = true
      let unsubscribe: (() => void) | null = null
      let timer: ReturnType<typeof setTimeout>
      // Detach both the flags listener and the timeout on settle: this runs
      // once per guarded navigation / login, so leaving either attached
      // accumulates handlers for the lifetime of the session.
      const settle = (value: boolean): void => {
        if (!settled) {
          settled = true
          unsubscribe?.()
          clearTimeout(timer)
          resolve(value)
        }
      }
      timer = setTimeout(
        () => settle(opts.freshFetch ? false : this.isFeatureEnabled(flag)),
        timeoutMs
      )
      const detach = this.onFeatureFlags(() => {
        if (!(opts.freshFetch && registering)) {
          settle(this.isFeatureEnabled(flag))
        }
      })
      registering = false
      // onFeatureFlags fires synchronously when flags are already loaded, so
      // settle() may have run before this assignment — in that case detach
      // here, since settle() saw a null unsubscribe and skipped it.
      if (settled) {
        detach()
      } else {
        unsubscribe = detach
      }
      if (opts.freshFetch) {
        try {
          posthog.reloadFeatureFlags()
        } catch (err) {
          // The reload never went out, so no fresh flags response is coming
          // and waiting for one would just burn the full timeout before
          // landing on the same answer. Settle now on the fail-closed value.
          console.error('[PostHogService] reloadFeatureFlags failed', err)
          settle(false)
        }
      }
    })
  }

  public reset (): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.reset()
    } catch (err) {
      console.error('[PostHogService] reset failed', err)
    }
  }
}
